import React, { useMemo } from 'react';

// constants
import {
  ALLOWED_BODY_NODE_TYPES,
  ALLOWED_FOOTER_NODE_TYPES,
  DEFAULT_MEDIA_SIZE,
} from '../../../constants/sequence';

// helpers
import { addLineBreake } from '../../../../inbox/helper';
import { cn } from '../../../../../helper/cn';

// components
import AudioMessagePreview from '../../../../inbox/components/conversation/AudioMessagePreview';
import DocumentMessagePreview from '../../../../inbox/components/conversation/DocumentMessagePreview';
import ImageMessagePreview from '../../../../inbox/components/conversation/ImageMessagePreview';
import VideoMessagePreview from '../../../../inbox/components/conversation/VideoMessagePreview';
import EdgeHandler from '../edge/Handler';
import SectionRow from './SectionRow';
import ContactMessage from './ContactMessage';
import Map from '../../../../../assets/customSVG/Map';
import { ICONS } from '../../../../../assets/icons';

const MEDIA_TYPES = [
  'MEDIA',
  'IMAGE',
  'VIDEO',
  'DOCUMENT',
  'AUDIO',
  'BUTTON',
  'LOCATION_REQUEST_MESSAGE',
  'FLOW',
  'CTA_URL',
  'CONTACTS',
];

const MediaMessage = ({
  previewMode = false,
  node,
  color,
  previewType = 'node',
  onEdgeMouseDown,
  checkIsEdgeConnected,
  allowToShowEdge = true,
  rootClassName = '',
  customBodyClassName = '',
  customFooterClassName = '',
  onButtonClick,
  allowToEditFlow,
  nodeCurrentStatus,
  getData = null,
}) => {
  const interactive = node?.data?.interactive || {};
  const plainData = node?.data || {};

  // Determine header type (IMAGE, VIDEO, etc.)
  const headerType = useMemo(() => {
    // For location request and contacts, we don't have a header
    if (['LOCATION_REQUEST_MESSAGE', 'CONTACTS'].includes(node?.type))
      return null;

    const rawType = ['BUTTON', 'LIST', 'FLOW', 'CTA_URL'].includes(node?.type)
      ? interactive.header?.type
      : plainData.type;
    return (rawType || '').toUpperCase();
  }, [node?.type, interactive.header?.type, plainData.type]);

  // Get CTA URL parameters
  const ctaUrlParams = useMemo(() => {
    if (node?.type !== 'CTA_URL') return null;
    return interactive.action?.parameters || {};
  }, [node?.type, interactive.action?.parameters]);

  const flowParams = useMemo(() => {
    if (node?.type !== 'FLOW') return null;
    return interactive.action?.parameters || {};
  }, [node?.type, interactive.action?.parameters]);

  // Pre–compute all possible header media data
  const headerData = useMemo(
    () => ({
      image: {
        link: interactive.header?.image?.link || plainData.image?.link || '',
        caption:
          interactive.header?.image?.caption || plainData.image?.caption || '',
      },
      video: {
        link: interactive.header?.video?.link || plainData.video?.link || '',
        caption:
          interactive.header?.video?.caption || plainData.video?.caption || '',
      },
      document: {
        link:
          interactive.header?.document?.link || plainData.document?.link || '',
        caption:
          interactive.header?.document?.caption ||
          plainData.document?.caption ||
          '',
      },
      audio: {
        link: interactive.header?.audio?.link || plainData.audio?.link || '',
        caption:
          interactive.header?.audio?.caption || plainData.audio?.caption || '',
      },
    }),
    [interactive, plainData]
  );

  // Compute body HTML
  const bodyHtml = useMemo(() => {
    const textSource = [
      'BUTTON',
      'LIST',
      'LOCATION_REQUEST_MESSAGE',
      'FLOW',
      'CTA_URL',
    ]?.includes(node?.type)
      ? interactive.body?.text
      : plainData.text?.body;
    return addLineBreake(textSource || '');
  }, [node?.type, interactive.body?.text, plainData.text?.body]);

  // Check if CTA URL is valid
  const isCtaUrlValid = useMemo(() => {
    if (node?.type !== 'CTA_URL') return true;
    const { display_text, url } = ctaUrlParams || {};
    if (!display_text?.trim() || !url?.trim()) return false;
    try {
      const urlObj = new URL(url);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch (e) {
      return false;
    }
  }, [node?.type, ctaUrlParams]);

  const isFlowValid = useMemo(() => {
    if (node?.type !== 'FLOW') return true;
    const cta = (flowParams?.flow_cta || '').trim();
    const identifier = (
      flowParams?.flow_id ||
      flowParams?.flow_identifier ||
      flowParams?.flow_name ||
      ''
    ).trim();
    return Boolean(cta) && cta.length <= 20 && Boolean(identifier);
  }, [flowParams, node?.type]);

  const buttons = interactive.action?.buttons || [];
  const sections = interactive.action?.sections || [];

  // Add Next Step draggable point
  const renderNextStepDraggable = () => {
    const nextStepPoint = node?.settings?.connections?.points?.find(
      (cp) => cp?.type === 'next_steps'
    );
    // Don't show for triggers or last node
    // if (node?.type === 'TRIGGER' || node?.is_last_node || !allowToShowEdge) {
    //   return null;
    // }

    // // For template nodes, the edge handler is already handled in the Template component
    // if (node?.data?.type === 'template') {
    //   return null;
    // }

    // // For media messages, the edge handler is handled in the MediaMessage component
    // if (['TEXT', 'MEDIA', 'BUTTON', 'LIST', 'LOCATION_REQUEST_MESSAGE', 'CTA_URL', 'CONTACTS'].includes(node?.type)) {
    //   return null;
    // }

    // For other node types, add the edge handler
    if (previewType === 'preview' || previewMode || node?.is_trigger || node?.is_last_node) {
      return null;
    }

    return (
      <div className="flex items-center justify-end pt-[10px] pb-[2px] pr-2">
        <div className="flex items-center gap-1 bg-white">
          <span className="text-[12px] font-medium">Next Step</span>
          <div className="absolute right-[-4px]">
            <EdgeHandler
              type="start"
              start_node_id={node?.uniq_id}
              isConnected={checkIsEdgeConnected?.(node?.id, 'next')}
              onEdgeMouseDown={({ position, payload }) => {
                if (!checkIsEdgeConnected?.(node?.id, 'next')) {
                  onEdgeMouseDown?.({
                    connection_point: {
                      id: nextStepPoint?.id || node?.id,
                      type: 'next_steps',
                    },
                    position,
                    payload: {
                      ...payload,
                      nodeId: nextStepPoint?.id || node?.id,
                      type: 'next_steps',
                    },
                  });
                }
              }}
              color={color}
              allowToEditFlow={allowToEditFlow}
              draggable={true}
              draggableType="next_steps"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={cn(
      'w-full h-full bg-white rounded-xl flex flex-col',
      previewMode ? 'p-1.5' : 'p-3',
      rootClassName
    )}>
      <div className="w-full h-full flex flex-col gap-2.5">
        {/* Header Media */}
        {headerType &&
          headerType !== 'NONE' &&
          MEDIA_TYPES.includes(node?.type) && (
            <div>
              {headerType === 'IMAGE' && (
                <ImageMessagePreview
                  url={headerData.image.link}
                  caption={headerData.image.caption}
                  rootClassName="max-w-64 min-w-full min-h-0 h-full p-0 rounded-lg overflow-hidden"
                  className="w-full p-0"
                  imageClassName={cn(
                    previewMode
                      ? 'h-full min-h-full'
                      : `h-[${DEFAULT_MEDIA_SIZE['image']}px]`
                  )}
                />
              )}
              {headerType === 'VIDEO' && (
                <VideoMessagePreview
                  url={headerData.video.link}
                  caption={headerData.video.caption}
                  rootClassName="max-w-64 min-w-full h-full min-h-0 !p-0 rounded-lg overflow-hidden"
                  className="w-full"
                  videoClassName={`h-[${DEFAULT_MEDIA_SIZE['video']}px]`}
                />
              )}
              {headerType === 'DOCUMENT' && (
                <DocumentMessagePreview
                  url={headerData.document.link}
                  caption={headerData.document.caption}
                  fileName={plainData.document?.filename}
                  rootClassName="max-w-64 min-w-full h-full min-h-0 !p-0 !rounded-2xl !overflow-hidden"
                  className="w-full"
                  documentClassName={`h-[${DEFAULT_MEDIA_SIZE['document']}px]`}
                />
              )}
              {headerType === 'AUDIO' && (
                <AudioMessagePreview
                  url={headerData.audio.link}
                  caption={headerData.audio.caption}
                  rootClassName="max-w-full min-w-full h-full min-h-0 !p-0 rounded-lg overflow-hidden !bg-transparent !shadow-none"
                  className="min-w-full w-full"
                  audioClassName={`w-full !h-[${DEFAULT_MEDIA_SIZE['audio']}px] py-1`}
                />
              )}
              {headerType === 'TEXT' && (
                <p
                  className={cn(
                    'w-full text-[0.813rem] text-[#2D3036] weight-semibold !break-words',
                    !previewMode && 'line-clamp-1 overflow-hidden',
                    customBodyClassName
                  )}
                >
                  {interactive.header?.text}
                </p>
              )}
            </div>
          )}

        {/* Body */}
        {ALLOWED_BODY_NODE_TYPES.includes(node?.type) && (
          <div
            className={cn(
              'w-full',
              node?.type === 'LOCATION_REQUEST_MESSAGE' && ''
            )}
          >
            <p
              className={cn(
                'w-full text-[0.813rem] text-[#2D3036] weight-medium !break-words',
                !previewMode && previewType !== 'preview' && 'line-clamp-1 overflow-hidden',
                customBodyClassName
              )}
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />

            {/* Location Request Button */}
            {node?.type === 'LOCATION_REQUEST_MESSAGE' && (
              <div className="relative w-full h-[1.875rem] mt-2">
                <div
                  className={cn(
                    'relative w-full h-full flex items-center border border-[#E8E8EA] bg-[#F8F9FC] rounded-[5px]',
                    ['dashboard', 'preview'].includes(previewType) &&
                      'cursor-pointer'
                  )}
                  onClick={() => {}}
                >
                  <div className="w-full h-full flex items-center justify-center gap-2 text-[#007AFF] h-7 weight-medium text-xs">
                    <Map color="#007AFF" width="18" height="18" />
                    Send Location
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        {ALLOWED_FOOTER_NODE_TYPES.includes(node?.type) &&
          interactive.footer?.text && (
            <p
              className={cn(
                'text-[0.813rem] text-[var(--font-400)] !break-words weight-medium',
                customFooterClassName
              )}
            >
              {(node?.type === 'BUTTON' ||
                node?.type === 'LIST' ||
                node?.type === 'FLOW' ||
                node?.type === 'CTA_URL' ||
                node?.type === 'CONTACTS') &&
                interactive.footer.text}
            </p>
          )}

        {/* Buttons */}
        {node?.type === 'BUTTON' && (
          <div className="flex flex-col gap-2.5">
            {buttons.map((b) => {
              const connection_point = { id: b.reply.id, type: 'action' };
              const isConnected = checkIsEdgeConnected
                ? checkIsEdgeConnected({
                    start_node_id: node?.uniq_id,
                    connection_point,
                  })
                : false;
              return (
                <div
                  key={b.reply.id}
                  className={cn(
                    'relative w-full h-[1.875rem] flex items-center border border-[#E8E8EA] bg-[#F8F9FC] rounded-[5px]',
                    ['dashboard', 'preview'].includes(previewType) &&
                      'cursor-pointer'
                  )}
                  onClick={() => onButtonClick?.(b)}
                >
                  <div className="relative w-full h-[1.875rem] text-[0.813rem] weight-medium text-[#007AFF] flex items-center justify-center">
                    {b.reply.title}
                  </div>
                  {allowToShowEdge && (
                    <div
                      id={b.reply.id}
                      className="absolute top-2 right-1 z-50"
                    >
                      <EdgeHandler
                        type="start"
                        start_node_id={node?.uniq_id}
                        connection_point={connection_point}
                        isConnected={isConnected}
                        onEdgeMouseDown={({ position, payload }) => {
                          if (!isConnected) {
                            onEdgeMouseDown?.({
                              connection_point,
                              position,
                              payload,
                            });
                          }
                        }}
                        color={color}
                        allowToEditFlow={allowToEditFlow}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* List */}
        {node?.type === 'LIST' && (
          <div className="w-full flex flex-col gap-2.5">
            {interactive.action?.button && (
              <div
                className="relative w-full h-[1.875rem] text-[0.813rem] weight-medium text-[#007AFF] flex items-center justify-center border border-[#E8E8EA] bg-[#F8F9FC] rounded-[5px]"
                onClick={() => getData?.(node)}
              >
                {interactive.action.button}
              </div>
            )}
            {!previewMode && (
              <div className="flex flex-col gap-2.5">
                {sections.map((section) => (
                  <div key={section.title} className="flex flex-col gap-2">
                    <h5 className="w-full h-[1.875rem] flex items-center overflow-hidden truncate text-[0.813rem] weight-bold text-[#2D3036]">
                      {section.title}
                    </h5>
                    <div className="flex flex-col gap-2">
                      {section.rows.map((row) => {
                        const connection_point = {
                          id: row.id,
                          type: 'action',
                        };
                        const isConnected = checkIsEdgeConnected
                          ? checkIsEdgeConnected({
                              start_node_id: node?.uniq_id,
                              connection_point,
                            })
                          : false;
                        return (
                          <div
                            key={row.id}
                            className={cn(
                              'relative w-full h-full min-h-[1.938rem] max-h-[2.438rem] flex items-center justify-center border-b border-gray-200',
                              ['dashboard', 'preview'].includes(previewType)
                                ? 'cursor-pointer'
                                : ''
                            )}
                            onClick={() => onButtonClick?.(row)}
                          >
                            <SectionRow
                              title={row.title || ''}
                              description={row.description || ''}
                            />
                            {allowToShowEdge && (
                              <EdgeHandler
                                type="start"
                                start_node_id={node?.uniq_id}
                                connection_point={connection_point}
                                isConnected={isConnected}
                                onEdgeMouseDown={({ position, payload }) => {
                                  if (!isConnected) {
                                    onEdgeMouseDown?.({
                                      connection_point,
                                      position,
                                      payload,
                                    });
                                  }
                                }}
                                color={color}
                                allowToEditFlow={allowToEditFlow}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Flow Button */}
        {node?.type === 'FLOW' && (
          <div className="w-full flex flex-col gap-2.5">
            <button
              type="button"
              className={cn(
                'relative w-full h-[1.875rem] flex items-center justify-center gap-2 border border-[#E8E8EA] rounded-[5px] bg-[#F0F4FF] text-[0.813rem] weight-semibold text-[#1F4FD8] transition-colors',
                isFlowValid ? 'hover:bg-[#e3ecff]' : 'opacity-60 cursor-not-allowed'
              )}
              onClick={() => {
                if (!isFlowValid) return;
                onButtonClick?.({
                  id:
                    flowParams?.flow_id ||
                    flowParams?.flow_identifier ||
                    'flow',
                  text: flowParams?.flow_cta || 'View Flow',
                  type: 'FLOW',
                  parameters: flowParams,
                });
              }}
              disabled={!isFlowValid}
            >
              {flowParams?.flow_cta || 'View Flow'}
            </button>
            {!isFlowValid && (
              <p className="text-xs text-red-500">
                Configure both the flow selection and button text to preview this button.
              </p>
            )}
          </div>
        )}

        {/* CTA URL Button */}
        {node?.type === 'CTA_URL' && (
          <div className="w-full flex flex-col gap-2.5">
            <div
              className={cn(
                'relative w-full cursor-pointer h-[1.875rem] flex items-center border border-[#E8E8EA] bg-[#F8F9FC] rounded-[5px]',
                isCtaUrlValid && ['dashboard', 'preview'].includes(previewType)
                  ? 'hover:bg-[#f0f2f7]'
                  : ''
              )}
              onClick={() => {
                if (!isCtaUrlValid || !ctaUrlParams?.url) return;

                // Open URL in a new tab
                window.open(ctaUrlParams.url, '_blank', 'noopener,noreferrer');

                // Also trigger the onButtonClick callback if provided
                onButtonClick?.({
                  reply: {
                    id: 'cta_url',
                    title: ctaUrlParams?.display_text || 'Open URL',
                    url: ctaUrlParams?.url,
                  },
                });
              }}
            >
              <div className="relative w-full h-[1.875rem] text-[0.813rem] weight-medium text-[#007AFF] flex items-center justify-center gap-1">
                <img
                  src={ICONS?.nounLink}
                  alt="button-icon"
                  className="size-[14px]"
                />

                {ctaUrlParams?.display_text || 'Open URL'}
              </div>
              {/* {!isCtaUrlValid && (
                  <p className="absolute -bottom-5 left-0 text-xs text-red-500 whitespace-nowrap">
                    {!ctaUrlParams?.display_text?.trim() ? 'Button text is required' : 'Valid URL is required'}
                  </p>
                )} */}
            </div>
          </div>
        )}

        {/* Contacts */}
        {node?.type === 'CONTACTS' && (
          <ContactMessage node={node} nodePreview={previewMode} />
        )}
      </div>

      {renderNextStepDraggable()}
    </div>
  );
};

export default MediaMessage;
